import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faGlobe, faUser, faEye, faEdit, faTrashAlt, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

// Renderizar texto con variables resaltadas {variable}
const renderMessageWithHighlights = (text, isDark) => {
    if (!text) return null;
    const parts = text.split(/(\{[a-zA-Z0-9_]+\})/g);

    return parts.map((part, index) => {
        if (part.startsWith('{') && part.endsWith('}')) {
            return (
                <span
                    key={index}
                    className="badge rounded px-1.5 py-0.5 mx-0.5 fw-bold"
                    style={{
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#dbeafe',
                        color: isDark ? '#93c5fd' : '#1d4ed8',
                        fontFamily: 'monospace',
                        fontSize: '0.82rem',
                        border: `1px solid ${isDark ? '#1d4ed8' : '#bfdbfe'}`
                    }}
                >
                    {part}
                </span>
            );
        }
        return <span key={index}>{part}</span>;
    });
};

const AutomaticMessageCard = ({ category, item, onPreview, onEdit, onDelete, onCopy, isCopied, isDark, token }) => {
    const categoryClave = category?.vClaveMensaje || category?.originalClave || category?.clave || item?.vClaveMensaje || 'MENSAJE';
    const [messages, setMessages] = React.useState(category?.messages || (item ? [item] : []));
    const [isLoadingMessages, setIsLoadingMessages] = React.useState(false);
    const [selectedMessageId, setSelectedMessageId] = React.useState(null);

    const fetchCategoryMessages = React.useCallback(async () => {
        if (!categoryClave || categoryClave === 'MENSAJE') return;
        setIsLoadingMessages(true);
        try {
            const resp = await fetch(`/api/SupportEngine/GetMensajesAutomaticosByClave?clave=${encodeURIComponent(categoryClave)}`, {
                headers: {
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                }
            });
            if (resp.ok) {
                const result = await resp.json();
                const list = result.data || result || [];
                if (Array.isArray(list)) {
                    setMessages(list);
                    if (list.length > 0) {
                        setSelectedMessageId(prev => (list.some(m => m.idMensaje === prev) ? prev : list[0].idMensaje));
                    }
                }
            }
        } catch (err) {
            console.error('Error al cargar mensajes de la categoría:', err);
        } finally {
            setIsLoadingMessages(false);
        }
    }, [categoryClave, token]);

    React.useEffect(() => {
        fetchCategoryMessages();
    }, [fetchCategoryMessages]);

    // Mantener seleccionado el mensaje activo
    const activeMessage = messages.find(m => m.idMensaje === selectedMessageId) || messages[0];
    const isCopiedActive = activeMessage ? isCopied === activeMessage.idMensaje : false;

    const handleDeleteActive = async () => {
        if (!activeMessage || !onDelete) return;
        await onDelete(activeMessage);
        fetchCategoryMessages();
    };

    const handleEditCategory = () => {
        if (onEdit) {
            onEdit({
                idCatMensajesAutomaticos: category?.idCatMensajesAutomaticos || 0,
                vClaveMensaje: categoryClave,
                messages: messages
            });
        }
    };

    return (
        <div
            className="rounded-3 border transition-all"
            style={{
                backgroundColor: isDark ? '#121e2d' : '#ffffff',
                borderColor: isDark ? '#233142' : '#e2e8f0',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                overflow: 'hidden'
            }}
        >
            {/* Header de la Categoría */}
            <div
                className="d-flex justify-content-between align-items-center px-3 py-2.5 border-bottom flex-wrap gap-2"
                style={{
                    backgroundColor: isDark ? '#182635' : '#f8fafc',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
            >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Clave de la Categoría */}
                    <span
                        className="badge px-2.5 py-1.5 fw-bold font-monospace d-inline-flex align-items-center"
                        style={{
                            fontSize: '0.86rem',
                            backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#eff6ff',
                            color: isDark ? '#60a5fa' : '#2563eb',
                            border: `1px solid ${isDark ? 'rgba(37, 99, 235, 0.4)' : '#bfdbfe'}`,
                            borderRadius: '6px'
                        }}
                    >
                        <FontAwesomeIcon icon={faFolder} className="me-1.5 opacity-75" />
                        {categoryClave}
                    </span>

                    {/* Contador de variantes */}
                    <span
                        className="badge rounded-pill px-2.5 py-1 fw-semibold"
                        style={{
                            fontSize: '0.74rem',
                            backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#475569'
                        }}
                    >
                        {isLoadingMessages
                            ? 'Cargando...'
                            : messages.length === 1
                            ? '1 destinatario'
                            : `${messages.length} destinatarios`}
                    </span>

                    {/* ID del mensaje activo actual si existe */}
                    {activeMessage && (
                        <span
                            className="badge rounded-pill px-2.5 py-1 fw-bold"
                            style={{
                                fontSize: '0.74rem',
                                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                color: isDark ? '#93c5fd' : '#1e40af'
                            }}
                        >
                            ID #{activeMessage.idMensaje}
                        </span>
                    )}
                </div>

                {/* Acciones */}
                <div className="d-flex align-items-center gap-1.5">
                    {activeMessage && (
                        <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1.5 border shadow-none"
                            onClick={() => onPreview(activeMessage)}
                            style={{
                                fontSize: '0.78rem',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                borderColor: isDark ? '#334155' : '#cbd5e1',
                                color: isDark ? '#94a3b8' : '#475569'
                            }}
                            title="Ver vista previa de WhatsApp"
                        >
                            <FontAwesomeIcon icon={faEye} />
                            <span className="d-none d-sm-inline">Vista Previa</span>
                        </button>
                    )}

                    <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center gap-1.5 border shadow-none"
                        onClick={handleEditCategory}
                        style={{
                            fontSize: '0.78rem',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            borderColor: isDark ? '#334155' : '#cbd5e1',
                            color: isDark ? '#60a5fa' : '#2563eb'
                        }}
                        title="Editar categoría y destinatarios"
                    >
                        <FontAwesomeIcon icon={faEdit} />
                        <span>Editar Categoría</span>
                    </button>

                    {activeMessage && onDelete && (
                        <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1.5 border shadow-none"
                            onClick={handleDeleteActive}
                            style={{
                                fontSize: '0.78rem',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2',
                                borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : '#fecaca',
                                color: isDark ? '#f87171' : '#dc2626'
                            }}
                            title="Eliminar este mensaje asignado"
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                            <span>Eliminar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Pestañas de destinatarios si la categoría tiene múltiples asignaciones */}
            <div className="px-3 pt-3 pb-0">
                {isLoadingMessages ? (
                    <div className="py-2 text-center">
                        <span className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                        <small className="text-muted">Cargando mensajes de {categoryClave}...</small>
                    </div>
                ) : messages.length > 1 ? (
                    <div className="d-flex align-items-center gap-1.5 flex-wrap pb-2 border-bottom" style={{ borderColor: isDark ? '#233142' : '#f1f5f9' }}>
                        <small className="text-muted fw-semibold me-1" style={{ fontSize: '0.75rem' }}>
                            Mensaje para:
                        </small>
                        {messages.map(msg => {
                            const isSelected = msg.idMensaje === activeMessage?.idMensaje;
                            const isGlobal = !msg.idUsuario;
                            const label = isGlobal ? 'Global' : (msg.vNombreUsuario || `Usuario #${msg.idUsuario}`);

                            return (
                                <button
                                    key={msg.idMensaje}
                                    type="button"
                                    onClick={() => setSelectedMessageId(msg.idMensaje)}
                                    className="badge rounded-pill px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1.5 border shadow-none"
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '0.78rem',
                                        backgroundColor: isSelected
                                            ? '#2563eb'
                                            : (isDark ? '#1e293b' : '#f8fafc'),
                                        color: isSelected
                                            ? '#ffffff'
                                            : (isDark ? '#cbd5e1' : '#475569'),
                                        borderColor: isSelected
                                            ? '#2563eb'
                                            : (isDark ? '#334155' : '#cbd5e1')
                                    }}
                                >
                                    <FontAwesomeIcon icon={isGlobal ? faGlobe : faUser} style={{ fontSize: '0.72rem' }} />
                                    <span>{label}</span>
                                </button>
                            );
                        })}
                    </div>
                ) : activeMessage ? (
                    <div className="d-flex align-items-center gap-2 pb-1">
                        <span
                            className="badge rounded-pill px-2.5 py-1 fw-semibold d-inline-flex align-items-center gap-1.5"
                            style={{
                                fontSize: '0.75rem',
                                backgroundColor: !activeMessage.idUsuario
                                    ? (isDark ? 'rgba(59, 130, 246, 0.18)' : '#e0e7ff')
                                    : (isDark ? 'rgba(16, 185, 129, 0.18)' : '#dcfce7'),
                                color: !activeMessage.idUsuario
                                    ? (isDark ? '#93c5fd' : '#3730a3')
                                    : (isDark ? '#6ee7b7' : '#166534'),
                                border: `1px solid ${!activeMessage.idUsuario ? (isDark ? 'rgba(59, 130, 246, 0.35)' : '#c7d2fe') : (isDark ? 'rgba(16, 185, 129, 0.35)' : '#bbf7d0')}`
                            }}
                        >
                            <FontAwesomeIcon icon={!activeMessage.idUsuario ? faGlobe : faUser} style={{ fontSize: '0.72rem' }} />
                            <span>{!activeMessage.idUsuario ? 'Destinatario: Global' : `Destinatario: ${activeMessage.vNombreUsuario || `Usuario #${activeMessage.idUsuario}`}`}</span>
                        </span>
                    </div>
                ) : null}
            </div>

            {/* Cuerpo del Mensaje Activo */}
            <div className="p-3">
                {activeMessage ? (
                    <div
                        className="p-3 rounded-2 position-relative"
                        style={{
                            backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                            border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.6',
                            fontSize: '0.88rem',
                            color: isDark ? '#f1f5f9' : '#1e293b',
                            wordBreak: 'break-word',
                            paddingBottom: '40px'
                        }}
                    >
                        {renderMessageWithHighlights(activeMessage.vMensajePersonalizado, isDark)}

                        {/* Botón Copiar */}
                        <div className="position-absolute" style={{ right: '12px', bottom: '10px' }}>
                            <button
                                type="button"
                                className={`btn btn-sm d-flex align-items-center gap-1.5 border shadow-none ${isCopiedActive ? 'btn-success text-white' : ''}`}
                                onClick={() => onCopy(activeMessage.idMensaje, activeMessage.vMensajePersonalizado)}
                                style={{
                                    fontSize: '0.75rem',
                                    padding: '3px 10px',
                                    borderRadius: '6px',
                                    backgroundColor: isCopiedActive
                                        ? '#10b981'
                                        : (isDark ? '#1e293b' : '#ffffff'),
                                    borderColor: isCopiedActive
                                        ? '#10b981'
                                        : (isDark ? '#334155' : '#cbd5e1'),
                                    color: isCopiedActive
                                        ? '#ffffff'
                                        : (isDark ? '#94a3b8' : '#475569')
                                }}
                                title="Copiar texto de la plantilla"
                            >
                                <FontAwesomeIcon icon={isCopiedActive ? faCheck : faCopy} />
                                <span>{isCopiedActive ? 'Copiado' : 'Copiar'}</span>
                            </button>
                        </div>
                    </div>
                ) : !isLoadingMessages ? (
                    <div className="p-3 text-center rounded-2 border border-dashed" style={{ borderColor: isDark ? '#334155' : '#cbd5e1' }}>
                        <small className="text-muted">
                            Esta categoría aún no tiene mensajes asignados. Haz clic en "Editar Categoría" para configurarla.
                        </small>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default AutomaticMessageCard;
